import { makeAutoObservable } from 'mobx';
import apiManage from '../../request';
import moment from 'moment';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import { HeadCell } from '../../component/table/table.component';
import { DeliverectLogDetail, DeliverectOrderStatus } from '../../models/deliverect-log-detail';
import { dateValidate } from '../../constants/tool-order';
import { Admin } from '../../models';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import _ from 'lodash';
import { ConnectedProject, GetWebsocketConnectedProjectsParams, UnSendOrderList } from '../../request/api/restaurant';
import { ConditionType } from '../../component/form/search-key-input.component';
import { CommonDialog } from '../../component/dialog/commonDialog';

/**
 * 日志数据管理
 */
export class DeliverectLogState {

    constructor() {
        makeAutoObservable(this);
        const startDate = new Date(new Date().setHours(4, 0, 0));
        const endDate = new Date(new Date(new Date().setDate(new Date().getDate() + 1)).setHours(4, 0, 0));
        this.sendDateTimeData = {
            startDate: moment(startDate).format('YYYY-MM-DD 04:00:00'),
            endDate: moment(endDate).format('YYYY-MM-DD 04:00:00')
        }
        this.pickerValue = [startDate, endDate]
    }

    // *********************
    // Attributes
    // *********************
    // 表格加载控制
    isTableLoading: boolean = false;
    // 操作对象选择的值  2.0 | Deliverect
    opTargetSelectValue: string = 'all';
    // 搜索筛选条件选择的键值  餐厅名称 | 集团名称
    searchNameSelectValue: string = 'group_nickname';
    // 日志类型筛选条件选择的值  订单日志 | 菜品日志 | 错误日志
    logTypeSelectValue: string = 'all';
    // 搜索输入框的值
    searchValue: string = '';
    // 视图操作时间筛选值
    sendDateTimeData: {
        // 开始时间
        startDate: string,
        // 结束时间
        endDate: string,
    } = {
            startDate: '',
            endDate: ''
        }
    // 视图选择框的值
    pickerValue: [Date | null, Date | null] = [
        null,
        null
    ]
    // 日期跨度
    dayDiff: number = 0;
    // 日志列表数据
    logLists: DeliverectLogDetail[] = [];
    // 排序参数
    extraSortParams: {
        sort_key: string,
        sort_value: string,
        sort_control: number,
    } = { sort_key: '', sort_value: '', sort_control: 0 };
    // 排序map
    sortInfoMap: Record<number | 'default', { value: string, sortControl: number }> = {
        0: { value: '-1', sortControl: 1 },
        1: { value: '1', sortControl: 2 },
        2: { value: '', sortControl: 0 },
        default: { value: '', sortControl: 0 }
    };
    // 要排序的参数map
    sortParamsMap: Record<'logSort' | 'default', { restaurant_nickname?: string, group_nickname?: string, database_port?: string }> = {
        // 集团
        logSort: {
            restaurant_nickname: 'restaurant_nickname',
            group_nickname: 'group_nickname',
        },
        default: {}
    };
    // 原始用户信息
    userInfo: Admin = {};
    // 编辑的用户信息
    editUserInfo: EditUserInfoProps = {}
    // 连接的应用
    connectedProject: ConnectedProject[] = []
    // 已对接的应用conditions
    paramCondition: ConditionType[] = [];
    // tab
    activeTabIndex = 0
    // 未发送的订单
    unSendOrderList: UnSendOrderList[] = [];
    // 删除二次确认弹窗
    dialog: CommonDialog = { open: false }
    // 未发送的订单详情
    detailDialogData: DetailDialogData = { open: false }


    // *********************
    // Functions
    // *********************
    /**
     * 初始化加载用
     */
    async init() {
        // 获取用户信息
        this.getUserInfo();
        this.fetchData();
    }

    async getUserInfo() {
        const account = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT) || {};
        const userInfo = await apiManage.get_admin_id_id(account._id || '');
        if (!userInfo?.error) {
            this.updateUserInfo(userInfo);
            this.updateEditUserInfo(_.pick(userInfo, ['client_id', 'client_secret']));
        }
    }
    /**
     * 开启加载获取数据
     */
    async fetchData() {
        this.setTableLoading(true);
        await this.getLogData();
        this.setTableLoading(false);
    }
    /**
     * 获取日志列表数据
     */
    async getLogData() {
        // 组装发送的参数
        const params = this.buildSendParams();
        // 请求接口
        const result = await apiManage.get_deliverect_log(params);
        // 请求成功
        if (!result.error) {
            this.setLogList(result?.[0]?.dbResults || []);
        }
        // 失败
        else {
            GlobalNotification.error(i18n.t('deliverect_log_page_text_description_failed_to_obtain_deliverect_logs'));
        }
    }
    /**
     * 组合发送的参数
     */
    buildSendParams() {
        const params = {
            pagination_per_page: 99999,
            pagination_page: 1,
            ...this.buildSortParams(),
            ...this.buildSearchParams(),
            ...this.buildDateTimeParams(),
            ...this.buildSelectValueParams()
        }
        return params;
    }
    /**
     * 构建排序传参
     * @returns
     */
    buildSortParams() {
        // 排序传参obj
        let sortObj = {} as { sort_key: string; sort_value?: number };
        const sortParams = this.sortParamsMap['logSort'];
        if (this.extraSortParams.sort_control && this.extraSortParams?.sort_value) {
            const sortKey = sortParams[this.extraSortParams?.sort_key as sortKeyType] || '';
            sortObj = {
                sort_key: sortKey,
                sort_value: +this.extraSortParams.sort_value || undefined,
            };
        }
        return sortObj;
    }
    /**
     * 构建搜索传参
     * @returns
     */
    buildSearchParams() {
        // 搜索obj
        let searchObj = {};
        if (this.searchValue !== '') {
            searchObj = {
                [this.searchNameSelectValue]: this.searchValue
            }
        }
        return searchObj;
    }
    /**
     * 构建时间范围传参
     * @returns
     */
    buildDateTimeParams() {
        // 时间范围传参
        const dateParams = {
            // start_time: moment(this.sendDateTimeData.startDate).format('YYYY-MM-DD HH:mm:ss'),
            // end_time: moment(this.sendDateTimeData.endDate).format('YYYY-MM-DD HH:mm:ss')
            date: `${moment(this.sendDateTimeData.startDate).toISOString()},${moment(this.sendDateTimeData.endDate).toISOString()}`
        }
        return dateParams;
    }
    /**
     * 构建下拉框选择筛选类型的参数
     * @returns
     */
    buildSelectValueParams() {
        const params = {
            // 操作对象
            operator: this.opTargetSelectValue === 'all' ? undefined : this.opTargetSelectValue,
            // 日志类型
            type: this.logTypeSelectValue === 'all' ? undefined : this.logTypeSelectValue
        };
        return params;
    }

    /**
     * 表格搜索函数
     * @param value 搜索值
     */
    async handleSearch() {
        await this.fetchData();
    }

    async handleSort(headCell: HeadCell) {
        // 排序值 value 1 正序  -1 倒序
        let value = '1';
        let sortControl = 0;
        // 排序对象
        const sortInfo = this.sortInfoMap[headCell.sortStatus || 0] || this.sortInfoMap['default'];
        // 排序值
        value = sortInfo?.value || '';
        // 排序键值控制
        sortControl = sortInfo?.sortControl || 0;
        // 设置排序表头状态
        this.setSortParams({
            sort_key: sortControl === 0 ? '' : headCell.id,
            sort_value: value,
            sort_control: sortControl
        });
        await this.fetchData();
    }

    /**
     * 日期筛选输入框改变函数
     * @param dates
     */
    handleDateTime(dates: [Date | null, Date | null]) {
        this.pickerValue = dates;
        // 校验日期是否设置正确
        const isDatePass = dates[0] && dates[1] && dateValidate(dates[0], dates[1]);
        // 日期正确  请求数据
        if (isDatePass) {
            this.setSendDateTimeData({
                startDate: moment(dates[0]).format('YYYY-MM-DD HH:mm:00'),
                endDate: moment(dates[1]).format('YYYY-MM-DD HH:mm:00')
            })
            this.fetchData();
        }
    }

    /**
     * 保存表单
     */
    async handleSave() {
        const newAccount = {
            _id: this.userInfo._id,
            ...this.editUserInfo,
        }
        const result = await apiManage.patch_admin_id_admin(newAccount)
        if (result && !result?.error) {
            GlobalNotification.success(`${i18n.t('dish_book_msg_save_success')}`);
            this.getUserInfo()
        } else {
            GlobalNotification.error(`${i18n.t('dish_book_msg_save_failed')}`);
        }
    }

    /**
     * 表单回调
     */
    onFormChange(value: string, key: string) {
        this.updateEditUserInfo({ ...this.editUserInfo, [key]: value })
    }

    // 获取连接的应用
    async getWebsocketConnectedProjects(): Promise<void> {
        this.setTableLoading(true)
        // 搜索obj
        const searchObj: GetWebsocketConnectedProjectsParams = {};
        this.paramCondition.forEach((item) => {
            searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
        })
        const result = await apiManage.get_websocket_connected_projects(searchObj);
        if (!result?.error) {
            this.updateConnectedProject(result)
        }
        this.setTableLoading(false)
    }

    // 获取未发送的订单

    async getOrderList(): Promise<void> {
        this.setTableLoading(true)
        // 搜索obj
        const searchObj: GetWebsocketConnectedProjectsParams = {};
        this.paramCondition.forEach((item) => {
            searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
        })
        const result = await apiManage.get_restaurant_d_order_condition({
            pagination_page: 1,
            pagination_per_page: 9999,
            ...searchObj
        });
        if (!result?.error) {
            this.unSendOrderList = result[0].dbResults || [];
        }
        this.setTableLoading(false)
    }


    /**
     * 删除未发送的订单
     * @param order 
     */
    async handleDeleteOrder(order: UnSendOrderList) {
        this.setTableLoading(true)
        const result = await apiManage.delete_deliverect_order_id(order._id);
        if (result && !result?.error) {
            GlobalNotification.success(i18n.t('global_text_operate_success'))
            this.getOrderList()
        }

    }

    // *********************
    // Constant Method
    // *********************
    // 获取表头排序状态值
    getSortStatus(key: string) {
        const listSort = this.extraSortParams;
        if (key === listSort.sort_key) {
            return listSort.sort_control
        }
        return 0;
    }

    /**
     * 设置排序参数
     * @param val 排序参数
     */
    setSortParams(val: typeof this.extraSortParams) {
        this.extraSortParams = val;
    }
    /**
     * 设置日志表格列表数据
     * @param value
     */
    setLogList(value: typeof this.logLists) {
        this.logLists = value;
    }
    /**
     * 设置表格加载
     * @param loading
     */
    setTableLoading(loading: boolean) {
        this.isTableLoading = loading;
    }
    /**
     * 设置时间筛选器的值
     * @param value
     */
    setSendDateTimeData(value: typeof this.sendDateTimeData) {
        this.sendDateTimeData = value;
    }
    /**
     * 设置搜索下拉框键值
     * @param value
     */
    async setOpTargetSelectValue(value: string) {
        this.opTargetSelectValue = value;
        await this.fetchData();
    }
    /**
     * 设置日志类型下拉框选择的值
     * @param value
     */
    async setLogTypeSelectValue(value: string) {
        this.logTypeSelectValue = value;
        await this.fetchData();
    }
    /**
     * 设置搜索的键值
     * @param value
     */
    setSearchNameSelectValue(value: string) {
        this.searchNameSelectValue = value;
    }
    /**
     * 设置搜索值
     * @param value
     */
    setSearchValue(value: string) {
        this.searchValue = value;
    }
    /**
     * 输入框输入时  更新搜索值
     * @param value
     */
    updateSearchValue(value: string) {
        this.searchValue = value;
    }

    /**
     * 更新
     * @param value 
     */
    updateUserInfo(value: Admin) {
        this.userInfo = value
    }


    /**
     * 更新
     * @param value 
     */
    updateEditUserInfo(value: EditUserInfoProps) {
        this.editUserInfo = value
    }




    /**
     * 更新
     * @param value 
     */
    updateConnectedProject(value: ConnectedProject[]) {
        this.connectedProject = value
    }

    /**
     * 条件查询
     */
    setParamConditions(value: ConditionType[]) {
        this.paramCondition = [...value]
        if (this.activeTabIndex === 1) {
            this.getWebsocketConnectedProjects()
        } else if (this.activeTabIndex === 2) {
            this.getOrderList()
        }
    }


    /**
     * 切换tab
     * @param value 
     */
    setActiveTabIndex(value: number) {
        this.activeTabIndex = value;
        if (value === 0) {
            this.fetchData()
        } else if (value === 1) {
            this.paramCondition = []
            this.getWebsocketConnectedProjects()
        } else {
            this.paramCondition = []
            this.getOrderList()
        }
    }


    /**
     * 二次确认弹窗
     * @param isOpen 
     */
    setDialog(value: CommonDialog) {
        this.dialog = value;
    }

    /**
     * 详情弹窗
     * @param isOpen 
     */
    setOpenDetailDialog(value: DetailDialogData) {
        this.detailDialogData = {
            ...this.detailDialogData,
            ...value
        };
    }
}

// TYPE  餐厅名称 | 集团名称
type sortKeyType = 'res_nickname' | 'group_name';

/**
 * 获取Deliverect平台订单状态
 */
export const orderStatusI18n = (status: DeliverectOrderStatus) => {
    const statusI18n = new Map([
        [DeliverectOrderStatus.CREATE, i18n.t('general_text_create')],
        [DeliverectOrderStatus.COMPLETE, i18n.t('general_text_complete')],
        [DeliverectOrderStatus.CANCEL, i18n.t('general_button_cancel')],
        [DeliverectOrderStatus.CANCEL2, i18n.t('general_button_cancel')],
        [DeliverectOrderStatus.ACCEPTED, i18n.t('general_text_accepted')]
    ])
    return statusI18n.get(status) || '';
}

type EditUserInfoProps = Pick<Admin, 'client_id' | 'client_secret'>

type DetailDialogData = {
    open?: boolean;
    data?: UnSendOrderList;
}